import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Reacts to product quantity changes and updates the quantity discount card.
 *
 * @typedef {Object} QuantityDiscountOfferRefs
 * @property {HTMLElement} offerTitle
 * @property {HTMLElement} offerMessage
 * @property {HTMLElement} offerCounter
 * @property {HTMLElement} offerProgress
 *
 * @extends {Component<QuantityDiscountOfferRefs>}
 */
class QuantityDiscountOfferComponent extends Component {
  requiredRefs = ['offerTitle', 'offerMessage', 'offerCounter', 'offerProgress'];
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    this.#updateOfferState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #handleQuantityUpdate = (event) => {
    const form = this.closest('product-form-component');
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;

    this.#updateOfferState();
  };

  #handleCartUpdate = () => {
    this.#updateOfferState();
  };

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    window.requestAnimationFrame(() => {
      this.#updateOfferState();
    });
  };

  #getThreshold() {
    return parseInt(this.dataset.threshold || '', 10) || 3;
  }

  #getDiscount() {
    return parseInt(this.dataset.discount || '', 10) || 10;
  }

  #replaceTokens(template, replacements) {
    return Object.entries(replacements).reduce((value, [token, replacement]) => {
      return value.replaceAll(`__${token}__`, String(replacement));
    }, template);
  }

  #getCurrentQuantity() {
    const form = this.closest('product-form-component');
    const quantityInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]'));

    if (!quantityInput) return 1;

    const cartQty = parseInt(quantityInput.getAttribute('data-cart-quantity') || '0', 10) || 0;
    const inputQty = parseInt(quantityInput.value || '1', 10) || 1;

    return cartQty + inputQty;
  }

  #updateOfferState() {
    const threshold = this.#getThreshold();
    const discount = this.#getDiscount();
    const quantity = this.#getCurrentQuantity();
    const safeQuantity = Math.max(quantity, 0);
    const remaining = Math.max(threshold - safeQuantity, 0);
    const reachedThreshold = safeQuantity >= threshold;
    const counterValue = Math.min(safeQuantity, threshold);
    const progressPercent = Math.min((safeQuantity / threshold) * 100, 100);
    const titleTemplate = this.dataset.titleTemplate || 'Buy __THRESHOLD__+ and save __DISCOUNT__%';
    const activeMessage = this.dataset.activeMessage || 'Eligible at checkout.';
    const messageOne = this.dataset.messageOne || 'Add 1 more to activate.';
    const messageManyTemplate = this.dataset.messageManyTemplate || 'Add __COUNT__ more to activate.';

    this.dataset.state = reachedThreshold ? 'active' : 'inactive';
    this.refs.offerCounter.textContent = `${counterValue}/${threshold}`;
    this.refs.offerProgress.style.width = `${progressPercent}%`;
    this.refs.offerTitle.textContent = this.#replaceTokens(titleTemplate, {
      THRESHOLD: threshold,
      DISCOUNT: discount,
    });

    if (reachedThreshold) {
      this.refs.offerMessage.textContent = activeMessage;
      return;
    }

    this.refs.offerMessage.textContent =
      remaining === 1
        ? messageOne
        : this.#replaceTokens(messageManyTemplate, { COUNT: remaining });
  }
}

if (!customElements.get('quantity-discount-offer')) {
  customElements.define('quantity-discount-offer', QuantityDiscountOfferComponent);
}
